import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import {
  FormattedDate,
  FormattedMessage,
  FormattedTime,
  injectIntl,
  intlShape,
} from '@edx/frontend-platform/i18n';
import { faCheckCircle as fasCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { faCheckCircle as farCheckCircle } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// import SidebarContext from '../../courseware/course/sidebar/SidebarContext';
import EffortEstimate from '../../shared/effort-estimate';
import { useModel } from '../../generic/model-store';
import messages from './messages';

const SequenceLink = ({
  id,
  intl,
  courseId,
  first,
  sequence,
  isActive,
  // hasActiveSection,
}) => {
  const {
    complete,
    description,
    due,
    showLink,
    title,
  } = sequence;
  const {
    userTimezone,
    isCourseCached,
  } = useModel('outline', courseId);

  // const { toggleSidebar, shouldDisplaySidebarOpen } = useContext(SidebarContext);

  // const handleSequenceClick = () => {
  //   if (toggleSidebar !== undefined && !shouldDisplaySidebarOpen) {
  //     toggleSidebar(null);
  //   }
  // };

  // const displaySequenceTitle = () => {
  //   if (!showLink) { return title; }

  //   return (
  //     <Link to={`/course/${courseId}/${id}`} onClick={handleSequenceClick}>{title}</Link>
  //   );
  // };

  const timezoneFormatArgs = userTimezone ? { timeZone: userTimezone } : {};

  const isPastDue = due && new Date(due) < new Date();

  const coursewareUrl = <Link to={`/course/${courseId}/${id}`}>{title}</Link>;
  const displayTitle = showLink ? coursewareUrl : title;

  const pastDueDateMessage = (
    <span>
      <FormattedMessage
        id="learning.outline.sequence-past-due-date-line1"
        defaultMessage="The due date was {assignmentDate} at {assignmentTime}."
        description="First line shown when the submission deadline has passed"
        values={{
          assignmentDate: (
            <FormattedDate
              key={`${id}-past-due-date`}
              day="numeric"
              month="long"
              year="numeric"
              value={due}
              {...timezoneFormatArgs}
            />
          ),
          assignmentTime: (
            <FormattedTime
              key={`${id}-past-due-time`}
              hour="2-digit"
              minute="2-digit"
              value={due}
              {...timezoneFormatArgs}
            />
          ),
        }}
      />
      <br />
      <FormattedMessage
        id="learning.outline.sequence-past-due-date-line2"
        defaultMessage="The deadline has passed - submission is no longer possible."
        description="Second line shown when the submission deadline has passed"
      />
    </span>
  );

  const dueDateMessage = (
    <FormattedMessage
      id="learning.outline.sequence-due-date-set"
      defaultMessage="{description} due {assignmentDue}"
      description="Used below an assignment title"
      values={{
        assignmentDue: (
          <FormattedTime
            key={`${id}-due`}
            day="numeric"
            month="short"
            year="numeric"
            timeZoneName="short"
            value={due}
            {...timezoneFormatArgs}
          />
        ),
        description: description || '',
      }}
    />
  );

  const noDueDateMessage = (
    <FormattedMessage
      id="learning.outline.sequence-due-date-not-set"
      defaultMessage="{description}"
      description="Used below an assignment title"
      values={{
        assignmentDue: (
          <FormattedTime
            key={`${id}-due`}
            day="numeric"
            month="short"
            year="numeric"
            timeZoneName="short"
            value={due}
            {...timezoneFormatArgs}
          />
        ),
        description: description || '',
      }}
    />
  );

  return (
    <li
      className={classNames('', { 'pt-2 border-top border-light': !first })}
      style={{
        backgroundColor: isActive ? 'rgba(190, 217, 226, .5)' : '',
      }}
      ref={(el) => (el && isActive) && el.style.setProperty('border-inline-start', '5px solid #0F5F83', 'important')}
    >
      <div className="row w-100 m-0 d-flex align-items-center">
        {
          !isCourseCached
          && (
          <div className="col-auto p-0" style={{ fontSize: '18px' }}>
            {complete ? (
              <>
                <i className="fa palette-css-icon" style={{ 'padding-top': '4px' }} title={intl.formatMessage(messages.completedSection)} />
                <FontAwesomeIcon
                  icon={fasCheckCircle}
                  fixedWidth
                  className="float-left mt-1 text-success"
                  aria-hidden="true"
                  title={intl.formatMessage(messages.completedSection)}
                />
              </>
            ) : (
              <>
                <i className="fa palette-css-icon" style={{ 'padding-top': '4px' }} title={intl.formatMessage(messages.incompleteSection)} />
                <FontAwesomeIcon
                  icon={farCheckCircle}
                  fixedWidth
                  className="float-left mt-1 text-gray-400"
                  aria-hidden="true"
                  title={intl.formatMessage(messages.incompleteSection)}
                />
              </>
            )}
          </div>
          )
        }
        <div className="col-10 p-0 ml-3 text-break">
          <span className="align-middle">{displayTitle}</span>
          <span className="sr-only">
            , {intl.formatMessage(complete ? messages.completedAssignment : messages.incompleteAssignment)}
          </span>
          <EffortEstimate className="ml-3 align-middle" block={sequence} />
        </div>
      </div>
      <div className="row w-100 m-0 ml-3 pl-3">
        <small className="text-body pl-2">
          {/* eslint-disable-next-line no-nested-ternary */}
          {due ? (isPastDue ? pastDueDateMessage : dueDateMessage) : noDueDateMessage}
        </small>
      </div>
    </li>
  );
};

SequenceLink.propTypes = {
  id: PropTypes.string.isRequired,
  intl: intlShape.isRequired,
  courseId: PropTypes.string.isRequired,
  first: PropTypes.bool.isRequired,
  sequence: PropTypes.shape().isRequired,
  isActive: PropTypes.bool,
  hasActiveSection: PropTypes.bool,
};

SequenceLink.defaultProps = {
  isActive: false,
  hasActiveSection: false,
};

export default injectIntl(SequenceLink);
